import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";

/**
 * RevenueBarChart — a daily bar series for one analytics workspace. Pure
 * presentation: it receives already-parsed numeric points and renders one bar
 * per day with the Business theme's primary color. The native MUI frame emits
 * `data-slot="chart"` for the existing test contract. It fabricates NO data —
 * an empty `data` array renders an empty chart, and the workspace owns the
 * honest "no data" copy above it.
 *
 * `dataKey` lets the same component back either the revenue ($) bars on the
 * Revenue workspace or the booking-count bars on the Booking workspace; the
 * caller supplies the human label + an axis/tooltip formatter so no number is
 * ever invented here.
 */

export interface RevenueBarPoint {
  /** Short day label for the X axis (e.g. "Jun 8"). */
  label: string;
  /** The numeric bar value (revenue in dollars, or a booking count). */
  value: number;
}

export function RevenueBarChart({
  data,
  seriesLabel,
  color,
  formatValue,
}: {
  data: RevenueBarPoint[];
  seriesLabel: string;
  color?: string;
  formatValue?: (v: number) => string;
}) {
  const theme = useTheme();
  const barColor = color ?? theme.palette.primary.main;

  return (
    <Box
      aria-label={`${seriesLabel} by day`}
      data-slot="chart"
      role="img"
      sx={{ height: { xs: 220, sm: 280 }, minWidth: 0, width: "100%" }}
    >
      <ResponsiveContainer
        height="100%"
        initialDimension={{ height: 220, width: 320 }}
        minHeight={220}
        minWidth={0}
        width="100%"
      >
        <BarChart data={data} margin={{ left: 8, right: 8, top: 8 }}>
          <CartesianGrid stroke={theme.palette.divider} vertical={false} />
          <XAxis
            axisLine={false}
            dataKey="label"
            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
            tickLine={false}
            tickMargin={8}
          />
          <Tooltip
            content={({ active, label, payload }) => {
              const value = payload?.[0]?.value;
              if (!active || value == null) return null;
              const numericValue = Number(value);
              return (
                <Paper elevation={8} sx={{ minWidth: 128, px: 1.5, py: 1 }}>
                  <Typography color="text.secondary" variant="caption">
                    {String(label ?? "")}
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }} variant="body2">
                    {seriesLabel}:{" "}
                    {formatValue ? formatValue(numericValue) : String(value)}
                  </Typography>
                </Paper>
              );
            }}
            cursor={false}
          />
          <Bar dataKey="value" fill={barColor} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
